import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import useIonicToast from '../../../hooks/useIonicToast';
import { useQuery } from '@tanstack/react-query';
import { getAllProducts } from '../getAllProducts';
import { RootState } from '../../../store/store';
import useProductCategory from './useProductCategory';
import { useQueryParams } from '../../../hooks/updateQueryParams';
import { PRODUCTS_QUANTITY } from '../../../Constants/productConstants';

type Props = {
  currentPage?: number;
  currentCategory?: string;
  currentQuantity?: string;
};

export default function useCatalog({
  currentPage = 1,
  currentCategory,
  currentQuantity,
}: Props) {
  const [totalPage, setTotalPage] = React.useState(1);
  const { presentIonToast } = useIonicToast();
  const userProfile = useSelector((state: RootState) => state.userProfile);
  const { signedAccessToken: authToken } = useSelector(
    (state: RootState) => state.userSession
  );
  const { activeStore, hasStore } = useSelector(
    (state: RootState) => state.userStores
  );
  const getCategories = useProductCategory();
  const [availableCategories, setAvailableCategories] = React.useState<
    { label: string; value: string; disabled?: boolean }[]
  >([]);

  const [filter, setFilter] = React.useState<{
    search?: string;
    quantity?: string;
    category?: string;
  }>({});

  //mutates url
  useQueryParams(filter);

  const fetchCategories = async () => {
    const array = [
      {
        label: 'all',
        value: 'all',
        disabled: true,
      },
    ];
    const categories = await getCategories();
    categories?.forEach((category: any) => {
      array.push({
        label: category.name,
        value: category.name,
        disabled: category.disabled,
      });
    });
    setAvailableCategories(array);
  };

  const {
    data: catalog,
    isLoading,
    isError,
    isPending,
    refetch,
  } = useQuery({
    queryKey: ['products-catalog', currentPage, filter],
    queryFn: async () => {
      try {
        if (!authToken) return;
        if (!activeStore?.id || !hasStore)
          throw new Error('You have not yet created your store');
        const res = await getAllProducts(
          authToken,
          activeStore?.id,
          currentPage,
          filter
        );
        setTotalPage(res?.pagination?.total_pages || 1);

        return res;
      } catch (error: any) {
        presentIonToast(error.message, 'error');
        throw error;
      }
    },
    enabled: Boolean(userProfile) && Boolean(authToken) && Boolean(activeStore),
  });

  const applyFilter = (name: keyof typeof filter, value: string) => {
    setFilter((prev) => {
      const { [name]: _, ...res } = prev;

      if ((value == 'all' && name !== 'search') || value == '') return res;
      return {
        [name]: value,
        ...res,
      };
    });
  };

  const removeFilter = (name: keyof typeof filter) => {
    setFilter((prev) => {
      const { [name]: _, ...res } = prev;
      return res;
    });
  };

  useEffect(() => {
    fetchCategories();

    if (currentCategory || currentQuantity)
      setFilter((prev) => {
        let value = { ...prev };
        if (currentCategory) value = { ...value, category: currentCategory };

        if (currentQuantity) {
          const refinedQuantity = Number(currentQuantity) || 0;

          let quantityBucket;
          if (refinedQuantity === PRODUCTS_QUANTITY.OUT_OF_STOCK) {
            quantityBucket = PRODUCTS_QUANTITY.OUT_OF_STOCK;
          } else if (refinedQuantity <= PRODUCTS_QUANTITY.LOW_ON_STOCK) {
            quantityBucket = PRODUCTS_QUANTITY.LOW_ON_STOCK;
          } else {
            quantityBucket = PRODUCTS_QUANTITY.IN_STOCK;
          }

          value = { ...value, quantity: `${quantityBucket}` };
        }

        return value;
      });
  }, []);

  const activeFilters = useMemo(() => {
    const filterValues = Object.entries(filter);
    return filterValues.map(([key, value]) => ({
      word: `${key}: ${value}`, // THIS MAKES ACTIVE FILTERS WORDS READABLE
      onRemove: () => removeFilter(key as keyof typeof filter),
    }));
  }, [filter]);

  return {
    filter,
    totalPage,
    catalog,
    isLoading,
    isError,
    isPending,
    availableCategories,
    activeFilters,
    refetch,
    applyFilter,
    removeFilter,
  };
}
